import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { HomeSEO } from '@/components/SEO';
import MarketingHeader, { industries } from '@/components/layout/MarketingHeader';
import MarketingFooter from '@/components/layout/MarketingFooter';
import FAQ from '@/components/FAQ';
import { 
  Star, 
  Sparkles, 
  Copy, 
  Check, 
  TrendingUp, 
  Shield, 
  Zap, 
  Users,
  ArrowRight,
  ChevronRight,
  MessageSquareText,
  Target,
  BarChart3
} from 'lucide-react';
import logoIcon from '@/assets/logo-icon.webp';

export default function LandingPage() {
  const navigate = useNavigate();

  const features = [
    {
      icon: Sparkles,
      title: 'AI-Powered Reviews',
      description: 'Generate authentic, SEO-optimized review suggestions tailored to your business using advanced AI technology.',
    },
    {
      icon: Target,
      title: 'Hyper-Personalized',
      description: 'Create category-specific reviews with custom keywords, USPs, and location data for maximum local SEO impact.',
    },
    {
      icon: Shield,
      title: 'Google-Compliant',
      description: 'All generated reviews follow Google Business Profile guidelines - no automation, no fake identities.',
    },
    {
      icon: Zap,
      title: 'Instant Generation',
      description: 'Generate multiple unique review suggestions in seconds. Copy and share with customers effortlessly.',
    },
    {
      icon: Users,
      title: 'Customer-Ready QR Codes',
      description: 'Share a unique link or QR code with customers to help them post genuine reviews quickly.',
    },
    {
      icon: BarChart3,
      title: 'Track Performance',
      description: 'Monitor review submissions, track engagement, and measure your reputation growth over time.',
    },
  ];

  const steps = [
    {
      number: '01',
      title: 'Set Up Your Business',
      description: 'Add your business details, industry, and service categories. Upload your logo to personalize the customer experience.',
      visual: 'onboarding',
    },
    {
      number: '02',
      title: 'Generate Reviews',
      description: 'Use AI to create authentic review suggestions. Customize tone, style, keywords, and length to match your brand voice.',
      visual: 'generate',
    },
    {
      number: '03',
      title: 'Share with Customers',
      description: 'Share your unique review link or QR code. Customers can browse and copy pre-written reviews that resonate with them.',
      visual: 'share',
    },
    {
      number: '04',
      title: 'Grow Your Reputation',
      description: 'Customers paste reviews on your Google Business Profile. Track submissions and watch your online presence flourish.',
      visual: 'track',
    },
  ];

  const testimonials = [
    {
      quote: "Review Plum transformed how we collect reviews. Our Google rating went from 4.1 to 4.7 in just 3 months!",
      author: "Sarah M.",
      role: "Dental Clinic Owner",
      rating: 5,
    },
    {
      quote: "The AI suggestions are incredibly authentic. Our customers actually enjoy the review process now.",
      author: "Michael R.",
      role: "Restaurant Manager",
      rating: 5,
    },
    {
      quote: "Finally, a review tool that's compliant and actually works. The QR code feature is a game-changer.",
      author: "Jennifer L.",
      role: "Spa & Wellness Center",
      rating: 5,
    },
  ];

  return (
    <div className="min-h-screen bg-background">
      {/* SEO Component */}
      <HomeSEO />

      {/* Navigation */}
      <MarketingHeader />

      {/* Hero Section */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-accent/20" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-32">
          <div className="text-center max-w-4xl mx-auto">
            <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-medium mb-8">
              <Sparkles className="w-4 h-4" />
              AI-Powered Review Generation
            </div>
            
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-foreground leading-tight mb-6">
              Turn Happy Customers into
              <br />
              <span className="gradient-text">5-Star Reviews</span>
            </h1>
            
            <p className="text-lg sm:text-xl text-muted-foreground mb-10 max-w-2xl mx-auto">
              Generate authentic, SEO-optimized review suggestions that your customers can personalize and post. 
              100% Google-compliant. Zero fake reviews.
            </p>
            
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Button 
                onClick={() => navigate('/login')}
                className="gradient-btn h-14 px-8 text-lg"
                size="lg"
              >
                Get Started
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              <Button 
                variant="outline"
                className="h-14 px-8 text-lg rounded-xl"
                size="lg"
                onClick={() => document.getElementById('how-it-works')?.scrollIntoView({ behavior: 'smooth' })}
              >
                See How It Works
              </Button>
            </div>

            {/* Trust Indicators */}
            <div className="flex flex-wrap items-center justify-center gap-6 mt-12 text-sm text-muted-foreground">
              <div className="flex items-center gap-2">
                <Check className="w-5 h-5 text-success" />
                Starts at ₹5,000/year
              </div>
              <div className="flex items-center gap-2">
                <Check className="w-5 h-5 text-success" />
                Google-compliant
              </div>
              <div className="flex items-center gap-2">
                <Check className="w-5 h-5 text-success" />
                Setup in 2 minutes
              </div>
            </div>
          </div>

          {/* Hero Visual - App Preview */}
          <div className="mt-16 relative">
            <div className="relative mx-auto max-w-5xl">
              {/* Decorative elements */}
              <div className="absolute -top-4 -left-4 w-72 h-72 bg-primary/20 rounded-full blur-3xl" />
              <div className="absolute -bottom-4 -right-4 w-72 h-72 bg-accent/30 rounded-full blur-3xl" />
              
              {/* App Preview Card */}
              <div className="relative bg-card rounded-2xl border border-border shadow-2xl p-6 sm:p-8">
                <div className="flex items-center gap-3 mb-6">
                  <div className="flex gap-1.5">
                    <div className="w-3 h-3 rounded-full bg-destructive/60" />
                    <div className="w-3 h-3 rounded-full bg-warning/60" />
                    <div className="w-3 h-3 rounded-full bg-success/60" />
                  </div>
                  <div className="flex-1 bg-muted rounded-lg h-8 flex items-center px-4">
                    <span className="text-xs text-muted-foreground">reviewplum.com/generate</span>
                  </div>
                </div>
                
                {/* Simulated Generate Page */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Input Panel */}
                  <div className="soft-card">
                    <h3 className="font-semibold mb-4 text-sm">Review Parameters</h3>
                    <div className="space-y-3">
                      <div className="bg-muted rounded-xl h-10 flex items-center px-4 text-sm text-muted-foreground">
                        Sunshine Dental Clinic
                      </div>
                      <div className="bg-muted rounded-xl h-10 flex items-center px-4 text-sm text-muted-foreground">
                        Teeth Whitening
                      </div>
                      <div className="bg-muted rounded-xl h-10 flex items-center px-4 text-sm text-muted-foreground">
                        professional, friendly, expert care
                      </div>
                      <Button className="gradient-btn w-full h-12 text-sm">
                        <Sparkles className="mr-2 h-4 w-4" />
                        Generate Reviews
                      </Button>
                    </div>
                  </div>
                  
                  {/* Output Panel */}
                  <div className="soft-card">
                    <h3 className="font-semibold mb-4 text-sm">Generated Reviews</h3>
                    <div className="space-y-3">
                      {[
                        "I had an amazing experience at Sunshine Dental! The team made me feel so comfortable during my teeth whitening procedure...",
                        "Professional service from start to finish. Dr. Smith and the entire staff went above and beyond. My smile has never looked better!",
                      ].map((text, i) => (
                        <div key={i} className="bg-background rounded-xl p-4 border border-border">
                          <div className="flex gap-1 mb-2">
                            {[1,2,3,4,5].map((star) => (
                              <Star key={star} className="w-3 h-3 fill-warning text-warning" />
                            ))}
                          </div>
                          <p className="text-xs text-muted-foreground line-clamp-2">{text}</p>
                          <Button size="sm" variant="outline" className="mt-2 h-7 text-xs rounded-lg">
                            <Copy className="mr-1 h-3 w-3" />
                            Copy
                          </Button>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 lg:py-32 bg-accent/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-foreground mb-4">
              Everything You Need to Grow Reviews
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              A complete toolkit designed specifically for local businesses to build and manage their online reputation.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, index) => (
              <div 
                key={index}
                className="soft-card group hover:border-primary/30 transition-all duration-300"
              >
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mb-4 group-hover:bg-primary/20 transition-colors">
                  <feature.icon className="w-6 h-6 text-primary" />
                </div>
                <h3 className="text-lg font-semibold text-foreground mb-2">{feature.title}</h3>
                <p className="text-muted-foreground text-sm">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section id="how-it-works" className="py-20 lg:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-foreground mb-4">
              How Review Plum Works
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Four simple steps to transform your customer feedback into powerful Google reviews.
            </p>
          </div>

          <div className="space-y-12 lg:space-y-24">
            {steps.map((step, index) => (
              <div 
                key={index}
                className={`flex flex-col ${index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'} items-center gap-8 lg:gap-16`}
              >
                {/* Content */}
                <div className="flex-1 text-center lg:text-left">
                  <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-primary/10 text-primary font-bold text-xl mb-4">
                    {step.number}
                  </div>
                  <h3 className="text-2xl font-bold text-foreground mb-4">{step.title}</h3>
                  <p className="text-muted-foreground text-lg">{step.description}</p>
                </div>

                {/* Visual */}
                <div className="flex-1 w-full max-w-lg">
                  <div className="soft-card bg-gradient-to-br from-card to-accent/30 p-6 sm:p-8">
                    {step.visual === 'onboarding' && (
                      <div className="space-y-4">
                        <div className="flex items-center gap-3 mb-6">
                          <div className="w-16 h-16 rounded-xl bg-primary/20 flex items-center justify-center">
                            <img src={logoIcon} alt="Logo" className="w-10 h-10" loading="lazy" />
                          </div>
                          <div>
                            <div className="font-semibold">Sunshine Dental</div>
                            <div className="text-sm text-muted-foreground">Healthcare • Los Angeles</div>
                          </div>
                        </div>
                        <div className="space-y-2">
                          <div className="bg-muted/50 rounded-lg h-10 animate-pulse" />
                          <div className="bg-muted/50 rounded-lg h-10 animate-pulse" style={{ animationDelay: '100ms' }} />
                          <div className="bg-muted/50 rounded-lg h-10 animate-pulse" style={{ animationDelay: '200ms' }} />
                        </div>
                      </div>
                    )}
                    
                    {step.visual === 'generate' && (
                      <div className="space-y-4">
                        <div className="flex gap-2 overflow-x-auto pb-2">
                          {['Teeth Whitening', 'Cleaning', 'Implants'].map((cat, i) => (
                            <span 
                              key={cat}
                              className={`px-4 py-2 rounded-xl text-sm whitespace-nowrap ${
                                i === 0 ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'
                              }`}
                            >
                              {cat}
                            </span>
                          ))}
                        </div>
                        <div className="flex items-center gap-3">
                          <Sparkles className="w-5 h-5 text-primary animate-pulse" />
                          <span className="text-sm font-medium">Generating 3 reviews...</span>
                        </div>
                        <div className="space-y-2">
                          <div className="bg-background rounded-lg p-3 border border-border">
                            <div className="flex gap-1 mb-1">
                              {[1,2,3,4,5].map((s) => (
                                <Star key={s} className="w-3 h-3 fill-warning text-warning" />
                              ))}
                            </div>
                            <div className="h-3 bg-muted rounded w-full" />
                            <div className="h-3 bg-muted rounded w-3/4 mt-1" />
                          </div>
                        </div>
                      </div>
                    )}
                    
                    {step.visual === 'share' && (
                      <div className="text-center space-y-4">
                        <div className="w-32 h-32 mx-auto bg-foreground rounded-xl p-2">
                          <div className="w-full h-full bg-background rounded-lg flex items-center justify-center">
                            <div className="grid grid-cols-5 gap-1">
                              {Array.from({ length: 25 }).map((_, i) => (
                                <div 
                                  key={i} 
                                  className={`w-4 h-4 rounded-sm ${Math.random() > 0.3 ? 'bg-foreground' : 'bg-transparent'}`}
                                />
                              ))}
                            </div>
                          </div>
                        </div>
                        <div className="text-sm text-muted-foreground">
                          Scan to leave a review
                        </div>
                        <div className="bg-muted rounded-lg px-4 py-2 text-xs break-all">
                          reviewplum.com/review/sunshine-dental
                        </div>
                      </div>
                    )}
                    
                    {step.visual === 'track' && (
                      <div className="space-y-4">
                        <div className="grid grid-cols-2 gap-3">
                          <div className="bg-background rounded-xl p-4 text-center border border-border">
                            <div className="text-2xl font-bold text-foreground">47</div>
                            <div className="text-xs text-muted-foreground">Reviews Generated</div>
                          </div>
                          <div className="bg-background rounded-xl p-4 text-center border border-border">
                            <div className="text-2xl font-bold text-success">32</div>
                            <div className="text-xs text-muted-foreground">Reviews Submitted</div>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <TrendingUp className="w-5 h-5 text-success" />
                          <span className="text-sm font-medium text-success">+0.4 rating increase this month</span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Industries Section */}
      <section id="industries" className="py-20 lg:py-32 bg-accent/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-foreground mb-4">
              Built for Every Industry
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Tailored review generation solutions for businesses across India.
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
            {industries.map((industry) => (
              <button
                key={industry.path}
                onClick={() => navigate(industry.path)}
                className="soft-card text-center hover:border-primary/30 transition-all duration-300 cursor-pointer group"
              >
                <div className="text-3xl mb-2">{industry.icon}</div>
                <div className="text-sm font-medium text-foreground group-hover:text-primary transition-colors">
                  {industry.name}
                </div>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 lg:py-32 bg-accent/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-foreground mb-4">
              Loved by Local Businesses
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              See how businesses like yours are growing their online reputation with Review Plum.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="soft-card">
                <div className="flex gap-1 mb-4">
                  {Array.from({ length: testimonial.rating }).map((_, i) => (
                    <Star key={i} className="w-5 h-5 fill-warning text-warning" />
                  ))}
                </div>
                <p className="text-foreground mb-6">"{testimonial.quote}"</p>
                <div>
                  <div className="font-semibold text-foreground">{testimonial.author}</div>
                  <div className="text-sm text-muted-foreground">{testimonial.role}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-20 lg:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-foreground mb-4">
              Simple, Transparent Pricing
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Choose the plan that fits your business. Start growing your reviews today.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {/* Starter Plan */}
            <div className="soft-card flex flex-col">
              <div className="mb-6">
                <h3 className="text-xl font-bold text-foreground mb-2">Starter</h3>
                <p className="text-sm text-muted-foreground">Perfect for small businesses getting started</p>
              </div>
              <div className="mb-6">
                <span className="text-4xl font-bold text-foreground">₹5,000</span>
                <span className="text-muted-foreground">/year</span>
              </div>
              <ul className="space-y-3 mb-8 flex-1">
                <li className="flex items-center gap-2 text-sm">
                  <Check className="w-5 h-5 text-success flex-shrink-0" />
                  <span>Up to 50 AI-generated reviews/month</span>
                </li>
                <li className="flex items-center gap-2 text-sm">
                  <Check className="w-5 h-5 text-success flex-shrink-0" />
                  <span>1 business location</span>
                </li>
                <li className="flex items-center gap-2 text-sm">
                  <Check className="w-5 h-5 text-success flex-shrink-0" />
                  <span>3 review categories</span>
                </li>
                <li className="flex items-center gap-2 text-sm">
                  <Check className="w-5 h-5 text-success flex-shrink-0" />
                  <span>QR code & shareable link</span>
                </li>
                <li className="flex items-center gap-2 text-sm">
                  <Check className="w-5 h-5 text-success flex-shrink-0" />
                  <span>Basic analytics dashboard</span>
                </li>
              </ul>
              <Button 
                onClick={() => navigate('/login')}
                variant="outline"
                className="w-full h-12 rounded-xl"
              >
                Get Started
              </Button>
            </div>

            {/* Growth Plan - Featured */}
            <div className="soft-card flex flex-col border-2 border-primary relative">
              <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                <span className="bg-primary text-primary-foreground text-xs font-semibold px-4 py-1.5 rounded-full">
                  Most Popular
                </span>
              </div>
              <div className="mb-6">
                <h3 className="text-xl font-bold text-foreground mb-2">Growth</h3>
                <p className="text-sm text-muted-foreground">For growing businesses with multiple services</p>
              </div>
              <div className="mb-6">
                <span className="text-4xl font-bold text-foreground">₹12,000</span>
                <span className="text-muted-foreground">/year</span>
              </div>
              <ul className="space-y-3 mb-8 flex-1">
                <li className="flex items-center gap-2 text-sm">
                  <Check className="w-5 h-5 text-success flex-shrink-0" />
                  <span>Up to 200 AI-generated reviews/month</span>
                </li>
                <li className="flex items-center gap-2 text-sm">
                  <Check className="w-5 h-5 text-success flex-shrink-0" />
                  <span>3 business locations</span>
                </li>
                <li className="flex items-center gap-2 text-sm">
                  <Check className="w-5 h-5 text-success flex-shrink-0" />
                  <span>Unlimited review categories</span>
                </li>
                <li className="flex items-center gap-2 text-sm">
                  <Check className="w-5 h-5 text-success flex-shrink-0" />
                  <span>Custom branding on review pages</span>
                </li>
                <li className="flex items-center gap-2 text-sm">
                  <Check className="w-5 h-5 text-success flex-shrink-0" />
                  <span>Advanced analytics & reports</span>
                </li>
                <li className="flex items-center gap-2 text-sm">
                  <Check className="w-5 h-5 text-success flex-shrink-0" />
                  <span>Priority email support</span>
                </li>
              </ul>
              <Button 
                onClick={() => navigate('/login')}
                className="gradient-btn w-full h-12"
              >
                Get Started
              </Button>
            </div>

            {/* Enterprise Plan */}
            <div className="soft-card flex flex-col">
              <div className="mb-6">
                <h3 className="text-xl font-bold text-foreground mb-2">Enterprise</h3>
                <p className="text-sm text-muted-foreground">For large businesses & agencies</p>
              </div>
              <div className="mb-6">
                <span className="text-4xl font-bold text-foreground">₹25,000</span>
                <span className="text-muted-foreground">/year</span>
              </div>
              <ul className="space-y-3 mb-8 flex-1">
                <li className="flex items-center gap-2 text-sm">
                  <Check className="w-5 h-5 text-success flex-shrink-0" />
                  <span>Unlimited AI-generated reviews</span>
                </li>
                <li className="flex items-center gap-2 text-sm">
                  <Check className="w-5 h-5 text-success flex-shrink-0" />
                  <span>Unlimited business locations</span>
                </li>
                <li className="flex items-center gap-2 text-sm">
                  <Check className="w-5 h-5 text-success flex-shrink-0" />
                  <span>White-label solution</span>
                </li>
                <li className="flex items-center gap-2 text-sm">
                  <Check className="w-5 h-5 text-success flex-shrink-0" />
                  <span>API access</span>
                </li>
                <li className="flex items-center gap-2 text-sm">
                  <Check className="w-5 h-5 text-success flex-shrink-0" />
                  <span>Custom integrations</span>
                </li>
                <li className="flex items-center gap-2 text-sm">
                  <Check className="w-5 h-5 text-success flex-shrink-0" />
                  <span>Dedicated account manager</span>
                </li>
              </ul>
              <Button 
                onClick={() => navigate('/login')}
                variant="outline"
                className="w-full h-12 rounded-xl"
              >
                Contact Sales
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 lg:py-32 bg-accent/30">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="soft-card bg-gradient-to-br from-primary/10 via-card to-accent/20 py-12 sm:py-16">
            <MessageSquareText className="w-16 h-16 mx-auto text-primary mb-6" />
            <h2 className="text-3xl sm:text-4xl font-bold text-foreground mb-4">
              Ready to Grow Your Reviews?
            </h2>
            <p className="text-lg text-muted-foreground mb-8 max-w-xl mx-auto">
              Join hundreds of local businesses using Review Plum to build their online reputation. 
              Plans start at just ₹5,000/year.
            </p>
            <Button 
              onClick={() => navigate('/login')}
              className="gradient-btn h-14 px-10 text-lg"
              size="lg"
            >
              Get Started Now
              <ChevronRight className="ml-2 h-5 w-5" />
            </Button>
            <p className="text-sm text-muted-foreground mt-4">
              Flexible plans • Cancel anytime • Setup in 2 minutes
            </p>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <FAQ />

      {/* Footer */}
      <MarketingFooter />
    </div>
  );
}
